import React, { useState, useEffect, useRef } from "react";
import { auth, db } from "./firebase";
import {
  collection, addDoc, onSnapshot, query, orderBy,
  getDoc, doc, updateDoc, deleteDoc, setDoc,
  arrayUnion, arrayRemove
} from "firebase/firestore";

const ADMIN_EMAIL = "honestpro77@gmail.com";
const REACTIONS = ["👍", "❤️", "😂", "🙏", "👏"];

export default function ChatRoom({ roomId, roomTitle, onBack }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [userNaam, setUserNaam] = useState("");
  const [membershipStatus, setMembershipStatus] = useState("pending");
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [pinnedMsg, setPinnedMsg] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [editMsg, setEditMsg] = useState(null);
  const [showReactions, setShowReactions] = useState(null);
  const [msgOptions, setMsgOptions] = useState(null);
  const [searchMode, setSearchMode] = useState(false);
  const [searchText, setSearchText] = useState("");

  const bottomRef = useRef(null);
  const typingTimeout = useRef(null);
  const uid = auth.currentUser?.uid;
  const isAdmin = auth.currentUser?.email === ADMIN_EMAIL;

  useEffect(() => {
    fetchUser();

    const unsubMsgs = onSnapshot(
      query(collection(db, "messages", roomId, "chats"), orderBy("createdAt", "asc")),
      (snap) => {
        setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      }
    );

    const unsubTyping = onSnapshot(collection(db, "messages", roomId, "typing"), (snap) => {
      const now = Date.now();
      setTypingUsers(snap.docs
        .filter(d => d.id !== uid && now - d.data().timestamp < 5000)
        .map(d => d.data().naam));
    });

    const unsubRoom = onSnapshot(doc(db, "chatRooms", roomId), (snap) => {
      if (snap.exists()) setPinnedMsg(snap.data().pinnedMessage || null);
      else setPinnedMsg(null);
    });

    return () => { unsubMsgs(); unsubTyping(); unsubRoom(); clearTyping(); };
  }, [roomId]);

  const fetchUser = async () => {
    const snap = await getDoc(doc(db, "users", uid));
    if (snap.exists()) {
      setUserNaam(snap.data().naam || "Member");
      setMembershipStatus(snap.data().membershipStatus || "pending");
    }
  };

  const handleTyping = (val) => {
    setText(val);
    setDoc(doc(db, "messages", roomId, "typing", uid), { naam: userNaam, timestamp: Date.now() });
    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(clearTyping, 3000);
  };

  const clearTyping = () => {
    try { deleteDoc(doc(db, "messages", roomId, "typing", uid)); } catch {}
  };

  const sendMessage = async () => {
    if (!text.trim() || sending || membershipStatus !== "active") return;
    setSending(true);
    try {
      if (editMsg) {
        await updateDoc(doc(db, "messages", roomId, "chats", editMsg.id), { text: text.trim(), edited: true });
        setEditMsg(null);
      } else {
        const payload = { text: text.trim(), senderUid: uid, senderNaam: userNaam, createdAt: Date.now(), reactions: {} };
        if (replyTo) payload.replyTo = replyTo;
        await addDoc(collection(db, "messages", roomId, "chats"), payload);
        setReplyTo(null);
      }
      setText("");
      clearTyping();
    } catch (err) { console.error(err); }
    setSending(false);
  };

  const handleReaction = async (msgId, emoji) => {
    const msgRef = doc(db, "messages", roomId, "chats", msgId);
    const snap = await getDoc(msgRef);
    if (!snap.exists()) return;
    const users = (snap.data().reactions || {})[emoji] || [];
    if (users.includes(uid)) {
      await updateDoc(msgRef, { [`reactions.${emoji}`]: arrayRemove(uid) });
    } else {
      await updateDoc(msgRef, { [`reactions.${emoji}`]: arrayUnion(uid) });
    }
    setShowReactions(null);
  };

  const deleteMessage = async (msgId) => {
    await deleteDoc(doc(db, "messages", roomId, "chats", msgId));
    setMsgOptions(null);
  };

  const pinMessage = async (msg) => {
    await setDoc(doc(db, "chatRooms", roomId), { pinnedMessage: { text: msg.text, senderNaam: msg.senderNaam } }, { merge: true });
    setMsgOptions(null);
  };

  const unpinMessage = async () => {
    await setDoc(doc(db, "chatRooms", roomId), { pinnedMessage: null }, { merge: true });
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const fmt = (ts) => new Date(ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  const fmtDate = (ts) => new Date(ts).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

  const displayMessages = searchText
    ? messages.filter(m => m.text?.toLowerCase().includes(searchText.toLowerCase()))
    : messages;

  let lastDate = null;

  return (
    <div style={S.container} onClick={() => { setMsgOptions(null); setShowReactions(null); }}>

      <div style={S.header}>
        <button onClick={onBack} style={S.backBtn}>←</button>
        <div style={{ flex: 1 }}>
          <p style={S.headerSub}>Group Chat</p>
          <p style={S.headerTitle}>{roomTitle}</p>
        </div>
        <button onClick={() => { setSearchMode(p => !p); setSearchText(""); }} style={S.iconBtn}>Search</button>
      </div>

      {searchMode && (
        <div style={S.searchBar}>
          <input autoFocus style={S.searchInput} placeholder="Messages search karein..." value={searchText} onChange={e => setSearchText(e.target.value)} />
        </div>
      )}

      {pinnedMsg && (
        <div style={S.pinnedBar}>
          <div style={{ flex: 1 }}>
            <p style={S.pinnedName}>Pinned · {pinnedMsg.senderNaam}</p>
            <p style={S.pinnedText}>{pinnedMsg.text}</p>
          </div>
          {isAdmin && <button onClick={unpinMessage} style={S.smallBtn}>✕</button>}
        </div>
      )}

      {membershipStatus !== "active" && (
        <div style={S.pendingBanner}>Sirf active members message kar sakte hain. Aapki membership pending hai.</div>
      )}

      <div style={S.msgArea}>
        {displayMessages.length === 0 && (
          <div style={S.emptyBox}>
            <p style={{ fontSize: 13, color: "#9CA3AF" }}>{searchText ? "Koi message nahi mila." : "Pehle message karein!"}</p>
          </div>
        )}

        {displayMessages.map((m) => {
          const isMine = m.senderUid === uid;
          const d = fmtDate(m.createdAt);
          const showDate = d !== lastDate;
          lastDate = d;
          const hasReactions = m.reactions && Object.values(m.reactions).some(a => a.length > 0);

          return (
            <React.Fragment key={m.id}>
              {showDate && <div style={S.dateDivider}><span style={S.dateChip}>{d}</span></div>}

              <div style={{ ...S.msgRow, justifyContent: isMine ? "flex-end" : "flex-start" }}>
                {!isMine && <div style={S.avatar}>{(m.senderNaam || "?")[0].toUpperCase()}</div>}

                <div style={{ maxWidth: "72%" }}>
                  {!isMine && <p style={S.senderName}>{m.senderNaam}</p>}

                  <div
                    onClick={e => { e.stopPropagation(); setMsgOptions(msgOptions === m.id ? null : m.id); setShowReactions(null); }}
                    style={{ ...S.bubble, background: isMine ? "#0D1B3E" : "#fff", color: isMine ? "#fff" : "#0D1B3E", border: isMine ? "none" : "1px solid #ECECEE", borderRadius: isMine ? "16px 4px 16px 16px" : "4px 16px 16px 16px" }}>
                    {m.replyTo && (
                      <div style={{ ...S.replyPreview, borderLeft: isMine ? "3px solid rgba(255,255,255,0.4)" : "3px solid #0D1B3E", background: isMine ? "rgba(255,255,255,0.08)" : "#F7F7F9" }}>
                        <p style={{ margin: 0, fontSize: 11, fontWeight: 700, opacity: 0.85 }}>{m.replyTo.senderNaam}</p>
                        <p style={{ margin: 0, fontSize: 11, opacity: 0.7 }}>{m.replyTo.text?.slice(0, 60)}{m.replyTo.text?.length > 60 ? "..." : ""}</p>
                      </div>
                    )}
                    <p style={S.msgText}>{m.text}</p>
                    {m.edited && <p style={{ margin: 0, fontSize: 10, opacity: 0.55 }}>(edited)</p>}
                  </div>

                  {hasReactions && (
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                      {Object.entries(m.reactions).map(([emoji, users]) =>
                        users.length > 0 ? (
                          <button key={emoji} onClick={() => handleReaction(m.id, emoji)}
                            style={{ ...S.reactionChip, border: users.includes(uid) ? "1px solid #0D1B3E" : "1px solid transparent", background: users.includes(uid) ? "#F0F0F4" : "#F7F7F9" }}>
                            {emoji} {users.length}
                          </button>
                        ) : null
                      )}
                    </div>
                  )}

                  <p style={{ ...S.timeText, textAlign: isMine ? "right" : "left" }}>{fmt(m.createdAt)}</p>
                </div>
              </div>

              {msgOptions === m.id && (
                <div onClick={e => e.stopPropagation()}
                  style={{ ...S.optionsMenu, alignSelf: isMine ? "flex-end" : "flex-start", marginRight: isMine ? 8 : 0, marginLeft: isMine ? 0 : 44 }}>
                  <button style={S.optBtn} onClick={() => { setShowReactions(m.id); setMsgOptions(null); }}>React</button>
                  <button style={S.optBtn} onClick={() => { setReplyTo({ id: m.id, text: m.text, senderNaam: m.senderNaam }); setMsgOptions(null); }}>Reply</button>
                  {isMine && <button style={S.optBtn} onClick={() => { setEditMsg({ id: m.id }); setText(m.text); setMsgOptions(null); }}>Edit</button>}
                  {(isMine || isAdmin) && <button style={{ ...S.optBtn, color: "#DC2626" }} onClick={() => deleteMessage(m.id)}>Delete</button>}
                  {isAdmin && <button style={S.optBtn} onClick={() => pinMessage(m)}>Pin</button>}
                </div>
              )}

              {showReactions === m.id && (
                <div onClick={e => e.stopPropagation()}
                  style={{ ...S.reactionPicker, alignSelf: isMine ? "flex-end" : "flex-start", marginRight: isMine ? 8 : 0, marginLeft: isMine ? 0 : 44 }}>
                  {REACTIONS.map(emoji => (
                    <button key={emoji} style={S.reactionPickBtn} onClick={() => handleReaction(m.id, emoji)}>{emoji}</button>
                  ))}
                </div>
              )}
            </React.Fragment>
          );
        })}

        {typingUsers.length > 0 && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 0" }}>
            <div style={S.avatar}>{typingUsers[0][0]?.toUpperCase()}</div>
            <div style={{ ...S.bubble, background: "#fff", color: "#9CA3AF", border: "1px solid #ECECEE", borderRadius: "4px 16px 16px 16px", padding: "8px 14px" }}>
              <span style={{ fontSize: 11 }}>{typingUsers.join(", ")} likh raha/rahi hai...</span>
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {replyTo && (
        <div style={S.contextBar}>
          <div style={{ flex: 1 }}>
            <p style={S.contextLabel}>{replyTo.senderNaam} ko reply</p>
            <p style={S.contextSub}>{replyTo.text?.slice(0, 60)}{replyTo.text?.length > 60 ? "..." : ""}</p>
          </div>
          <button onClick={() => setReplyTo(null)} style={S.smallBtn}>✕</button>
        </div>
      )}

      {editMsg && (
        <div style={S.contextBar}>
          <p style={{ ...S.contextLabel, flex: 1 }}>Message edit ho raha hai</p>
          <button onClick={() => { setEditMsg(null); setText(""); }} style={S.smallBtn}>✕</button>
        </div>
      )}

      <div style={S.inputRow}>
        <textarea
          style={{ ...S.input, background: membershipStatus !== "active" ? "#F7F7F9" : "#FAFAFA" }}
          placeholder={membershipStatus !== "active" ? "Sirf active members..." : "Message likhein..."}
          value={text}
          onChange={e => handleTyping(e.target.value)}
          onKeyDown={handleKey}
          disabled={membershipStatus !== "active"}
          rows={1}
        />
        <button
          onClick={sendMessage}
          disabled={!text.trim() || sending || membershipStatus !== "active"}
          style={{ ...S.sendBtn, opacity: (!text.trim() || membershipStatus !== "active") ? 0.4 : 1 }}>
          →
        </button>
      </div>
    </div>
  );
}

const S = {
  container: { display: "flex", flexDirection: "column", height: "100vh", background: "#F7F7F9", fontFamily: "-apple-system, 'Segoe UI', sans-serif" },
  header: { background: "linear-gradient(135deg, #0D1B3E, #1A2F5A)", padding: "14px 16px", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 },
  backBtn: { background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 10, padding: "8px 12px", color: "#fff", cursor: "pointer", fontSize: 14 },
  headerSub: { color: "rgba(255,255,255,0.45)", fontSize: 10, margin: 0 },
  headerTitle: { color: "#fff", margin: 0, fontSize: 14, fontWeight: 700 },
  iconBtn: { background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 10, padding: "7px 10px", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 600 },
  searchBar: { background: "#fff", padding: "8px 12px", borderBottom: "1px solid #ECECEE" },
  searchInput: { width: "100%", padding: "8px 14px", borderRadius: 20, border: "1px solid #ECECEE", fontSize: 13, outline: "none", boxSizing: "border-box", background: "#FAFAFA" },
  pinnedBar: { background: "#fff", borderBottom: "1px solid #ECECEE", padding: "8px 14px", display: "flex", alignItems: "center", gap: 10 },
  pinnedName: { margin: 0, fontSize: 10, fontWeight: 700, color: "#0D1B3E" },
  pinnedText: { margin: 0, fontSize: 12, color: "#6B7280" },
  pendingBanner: { background: "#FFFBEB", padding: "8px 14px", fontSize: 12, color: "#92400E", textAlign: "center" },
  msgArea: { flex: 1, overflowY: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 2 },
  emptyBox: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", marginTop: 80 },
  dateDivider: { display: "flex", justifyContent: "center", margin: "10px 0" },
  dateChip: { background: "#fff", border: "1px solid #ECECEE", borderRadius: 12, padding: "3px 12px", fontSize: 11, color: "#9CA3AF" },
  msgRow: { display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 2 },
  avatar: { width: 28, height: 28, borderRadius: "50%", background: "#0D1B3E", color: "#fff", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  senderName: { margin: "0 0 2px", fontSize: 11, color: "#9CA3AF", fontWeight: 600, paddingLeft: 4 },
  bubble: { padding: "9px 13px", cursor: "pointer" },
  replyPreview: { borderRadius: 6, padding: "5px 8px", marginBottom: 6 },
  msgText: { margin: 0, fontSize: 13, lineHeight: 1.5, wordBreak: "break-word" },
  timeText: { margin: "3px 4px 0", fontSize: 10, color: "#9CA3AF" },
  reactionChip: { padding: "2px 8px", borderRadius: 12, fontSize: 12, cursor: "pointer" },
  optionsMenu: { background: "#fff", border: "1px solid #ECECEE", borderRadius: 12, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", padding: 6, display: "flex", flexDirection: "column", gap: 2, zIndex: 10, marginBottom: 4, minWidth: 140 },
  optBtn: { background: "none", border: "none", padding: "8px 12px", fontSize: 13, cursor: "pointer", textAlign: "left", borderRadius: 8, color: "#0D1B3E" },
  reactionPicker: { background: "#fff", border: "1px solid #ECECEE", borderRadius: 24, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", padding: "8px 12px", display: "flex", gap: 6, marginBottom: 4 },
  reactionPickBtn: { background: "none", border: "none", fontSize: 20, cursor: "pointer", padding: 2 },
  contextBar: { background: "#fff", borderTop: "1px solid #ECECEE", padding: "8px 14px", display: "flex", alignItems: "center", gap: 10 },
  contextLabel: { margin: 0, fontSize: 11, fontWeight: 700, color: "#0D1B3E" },
  contextSub: { margin: 0, fontSize: 12, color: "#9CA3AF" },
  smallBtn: { background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 14 },
  inputRow: { display: "flex", gap: 8, padding: "10px 14px", background: "#fff", borderTop: "1px solid #ECECEE", flexShrink: 0, alignItems: "flex-end" },
  input: { flex: 1, padding: "10px 14px", borderRadius: 20, border: "1px solid #ECECEE", fontSize: 13, outline: "none", resize: "none", fontFamily: "-apple-system, 'Segoe UI', sans-serif", maxHeight: 100 },
  sendBtn: { width: 42, height: 42, borderRadius: "50%", background: "#0D1B3E", border: "none", color: "#fff", fontSize: 18, cursor: "pointer", flexShrink: 0 },
};